import { useState, useEffect, useCallback } from 'react'
import { supabase } from '@/lib/supabase'
import { useSelectedSchool } from '@/contexts/SelectedSchoolContext'

export interface SchoolAffiliationRequest {
  id: string
  instructorId: string
  instructorName: string | null
  instructorEmail: string | null
  status: 'pending' | 'approved' | 'rejected'
  hourlyRate: number | null
  requestedAt: string
}

interface RespondResult {
  ok: boolean
  code?: string
  approved?: boolean
}

/**
 * Contractor affiliation requests (migration 078) for the school currently selected in the
 * shared page-level switcher (SelectedSchoolContext) — RLS "contractor_affiliation_requests:
 * school admin read" scopes this to schools the caller actually administers (is_school_admin_for),
 * same as every other school-admin list in this app. respond() approves (with an hourly
 * rate) or rejects via the respond-contractor-affiliation-request Edge Function, which is
 * the only path that ever creates the resulting school_memberships row.
 */
export function useSchoolAffiliationRequests() {
  const { selectedSchoolId } = useSelectedSchool()
  const [requests, setRequests] = useState<SchoolAffiliationRequest[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [respondingId, setRespondingId] = useState<string | null>(null)

  const fetchRequests = useCallback(async () => {
    if (!selectedSchoolId) { setRequests([]); setLoading(false); return }
    setLoading(true)
    setError(null)
    try {
      // instructor_name/instructor_email are snapshotted onto the row at request time
      // (migration 079), not joined live — RLS correctly blocks a school admin reading
      // the profile of an instructor who isn't yet an active member of their school,
      // which is exactly the pending-request state this panel shows.
      const { data, error: fetchErr } = await supabase
        .from('contractor_affiliation_requests')
        .select('id, instructor_id, status, hourly_rate, requested_at, instructor_name, instructor_email')
        .eq('school_id', selectedSchoolId)
        .order('requested_at', { ascending: false })

      if (fetchErr) { setError('Could not load affiliation requests.'); return }
      if (!data || data.length === 0) { setRequests([]); return }

      setRequests(data.map(row => ({
        id: row.id,
        instructorId: row.instructor_id,
        instructorName: row.instructor_name ?? null,
        instructorEmail: row.instructor_email ?? null,
        status: row.status,
        hourlyRate: row.hourly_rate === null || row.hourly_rate === undefined ? null : Number(row.hourly_rate),
        requestedAt: row.requested_at,
      })))
    } finally {
      setLoading(false)
    }
  }, [selectedSchoolId])

  useEffect(() => { fetchRequests() }, [fetchRequests])

  const respond = useCallback(async (requestId: string, approve: boolean, hourlyRate: number | null): Promise<RespondResult> => {
    setRespondingId(requestId)
    try {
      const { data, error: fnError } = await supabase.functions.invoke('respond-contractor-affiliation-request', {
        body: { requestId, approve, hourlyRate },
      })
      if (fnError) return { ok: false, code: 'internal_error' }
      if (data?.ok) await fetchRequests()
      return data as RespondResult
    } finally {
      setRespondingId(null)
    }
  }, [fetchRequests])

  return { requests, loading, error, respondingId, respond, refresh: fetchRequests }
}
